/* TheArtFolio discovery — server-filtered picks rendered through FrameVisualiser */
(function () {
  "use strict";

  var POOL_LIMIT = 48;
  // Tunable Stage 4 choice: keep the visitor's frame/mount/scene while auditioning art.
  var PRESERVE_OPTIONS_ON_REMIX = true;
  // Future tuning: set above 0 for occasional unfiltered wildcard picks.
  // Kept off for Stage 3 so every result strictly respects active filters.
  var WILDCARD_CHANCE = 0;

  // Representative site taxonomy colours. Phase 1 maps local image pixels to
  // these existing product tags; the uploaded image itself is never transmitted.
  var COLOUR_TAGS = {
    Black: "#1c1b19",
    Charcoal: "#4a4742",
    White: "#f4f1ea",
    Grey: "#9b958b",
    Silver: "#c8c6c2",
    Blue: "#3a5a8c",
    Navy: "#2a3550",
    Teal: "#2f7e7e",
    Green: "#5f7d4f",
    Sage: "#7a8a6a",
    Yellow: "#e3c34a",
    Gold: "#c9a24a",
    Orange: "#d08a3e",
    Rust: "#a85a32",
    Red: "#b23b34",
    Pink: "#d98ba6",
    Purple: "#7a5c9e",
    Brown: "#6f5037",
    Burgundy: "#6e2c38",
    Beige: "#d8c9ae",
    Cream: "#ece3cf",
    Ochre: "#c08a2e"
  };

  function hexToRgb(hex) {
    var value = String(hex || "").replace("#", "");
    if (value.length !== 6) return { r: 0, g: 0, b: 0 };
    return {
      r: parseInt(value.slice(0, 2), 16),
      g: parseInt(value.slice(2, 4), 16),
      b: parseInt(value.slice(4, 6), 16)
    };
  }

  function rgbToLab(rgb) {
    function linear(value) {
      value /= 255;
      return value > 0.04045 ? Math.pow((value + 0.055) / 1.055, 2.4) : value / 12.92;
    }
    var r = linear(rgb.r);
    var g = linear(rgb.g);
    var b = linear(rgb.b);
    var x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
    var y = r * 0.2126 + g * 0.7152 + b * 0.0722;
    var z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
    function pivot(value) {
      return value > 0.008856 ? Math.pow(value, 1 / 3) : (7.787 * value) + (16 / 116);
    }
    x = pivot(x);
    y = pivot(y);
    z = pivot(z);
    return { l: (116 * y) - 16, a: 500 * (x - y), b: 200 * (y - z) };
  }

  function extractedColourToTag(hex) {
    var source = rgbToLab(hexToRgb(hex));
    var nearest = "Grey";
    var nearestDistance = Infinity;
    Object.keys(COLOUR_TAGS).forEach(function (tag) {
      var target = rgbToLab(hexToRgb(COLOUR_TAGS[tag]));
      var distance = Math.sqrt(
        Math.pow(source.l - target.l, 2) +
        Math.pow(source.a - target.a, 2) +
        Math.pow(source.b - target.b, 2)
      );
      if (distance < nearestDistance) {
        nearest = tag;
        nearestDistance = distance;
      }
    });
    return nearest;
  }

  function colourKey(value) {
    var key = String(value || "").trim().toLowerCase();
    if (key === "gray" || key === "grey") return "gray";
    if (key === "rose gold" || key === "rose") key = "pink";
    if (key === "tan" || key === "clay") key = "beige";
    if (key === "olive") key = "sage";
    if (key === "turquoise") key = "teal";
    if (key === "bronze") key = "brown";
    if (key === "multi" || key === "multicolour") key = "multicolor";
    return key;
  }

  function handleize(value) {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function colourCollectionHandle(value) {
    var key = colourKey(value);
    if (!key) return "";
    if (key === "grey") key = "gray";
    if (key === "multi") key = "multicolor";
    return key + "-art-prints";
  }

  // Phase 2 replacement point: keep this interface and replace tag overlap
  // with true product-palette hex distance once structured palettes exist.
  function findMatches(palette, candidates) {
    var wanted = (palette && palette.tags || []).map(colourKey).filter(Boolean);
    var uniqueWanted = wanted.filter(function (tag, index) {
      return wanted.indexOf(tag) === index;
    });
    if (!uniqueWanted.length) return candidates.slice();
    return candidates.map(function (product, index) {
      var productColours = (product.colours || []).map(colourKey);
      var score = uniqueWanted.reduce(function (total, tag) {
        return total + (productColours.indexOf(tag) !== -1 ? 1 : 0);
      }, 0);
      return { product: product, score: score, index: index };
    }).filter(function (entry) {
      return entry.score > 0;
    }).sort(function (a, b) {
      return b.score - a.score || a.index - b.index;
    }).map(function (entry) {
      entry.product._colourMatchScore = entry.score;
      return entry.product;
    });
  }

  function productMoodList(product) {
    var raw = product && product.moods;
    if (Array.isArray(raw)) {
      return raw.map(function (item) {
        return String(item && (item.label || item.value || item) || "").toLowerCase().trim();
      }).filter(Boolean);
    }
    return String(raw || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
  }

  function scoreSeedAffinity(seed, product) {
    if (!seed || !product) return 0;
    var score = 0;
    var seedGenre = String(seed.genre || "").toLowerCase().trim();
    var candGenre = String(product.genre || "").toLowerCase().trim();
    if (seedGenre && candGenre) {
      if (seedGenre === candGenre) score += 12;
      else if (candGenre.indexOf(seedGenre) !== -1 || seedGenre.indexOf(candGenre) !== -1) score += 6;
    }

    var seedColours = (seed.colours || []).map(colourKey).filter(Boolean);
    var candColours = (product.colours || []).map(colourKey).filter(Boolean);
    seedColours.forEach(function (colour) {
      if (candColours.indexOf(colour) !== -1) score += 8;
    });

    var seedMoods = productMoodList(seed);
    var candMoods = productMoodList(product);
    seedMoods.forEach(function (mood) {
      if (candMoods.indexOf(mood) !== -1) score += 6;
    });

    var seedOrient = String(seed.orientation || "").toLowerCase();
    var candOrient = String(product.orientation || "").toLowerCase();
    if (seedOrient && candOrient && seedOrient === candOrient) score += 2;

    var seedArtist = String(seed.artist || "").toLowerCase();
    var candArtist = String(product.artist || "").toLowerCase();
    if (seedArtist && candArtist && seedArtist === candArtist) score += 3;

    var seedTags = (seed.tags || []).map(function (t) { return String(t).toLowerCase(); });
    var candTags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
    var shared = 0;
    seedTags.forEach(function (tag) {
      if (tag.length < 3) return;
      if (tag === "print" || tag === "prints" || tag === "framed" || tag === "art") return;
      if (candTags.indexOf(tag) !== -1) shared += 1;
    });
    score += Math.min(shared, 4) * 4;

    return score;
  }

  function pickAffinedCandidate(candidates, seed, lastId) {
    var list = (candidates || []).slice();
    if (!list.length) return null;
    if (!seed) {
      return list[Math.floor(Math.random() * list.length)] || null;
    }
    var scored = list.map(function (product, index) {
      return { product: product, score: scoreSeedAffinity(seed, product), index: index };
    }).sort(function (a, b) {
      return b.score - a.score || a.index - b.index;
    });
    var best = scored[0] ? scored[0].score : 0;
    var floor = Math.max(0, best - 6);
    var top = scored.filter(function (entry) {
      return entry.score >= floor;
    }).slice(0, 8);
    if (lastId != null && top.length > 1) {
      var without = top.filter(function (entry) {
        return String(entry.product.id) !== String(lastId);
      });
      if (without.length) top = without;
    }
    if (!top.length) return list[0];
    // Soft randomness among near-best matches so Remix still feels exploratory
    var pick = top[Math.floor(Math.random() * top.length)];
    return pick.product;
  }

  function initDiscover(root) {
    var form = root.querySelector("[data-discover-filters]");
    var colour = root.querySelector('[data-discover-filter="colour"]');
    var category = root.querySelector('[data-discover-filter="category"]');
    var orientation = root.querySelector('[data-discover-filter="orientation"]');
    var clearBtn = root.querySelector("[data-discover-clear]");
    var result = root.querySelector("[data-discover-result]");
    var loading = root.querySelector("[data-discover-loading]");
    var empty = root.querySelector("[data-discover-empty]");
    var emptyDefault = root.querySelector("[data-discover-empty-default]");
    var reactionEmpty = root.querySelector("[data-discover-reaction-empty]");
    var sourceEmpty = root.querySelector("[data-discover-source-empty]");
    var sourceEmptyTitle = root.querySelector("[data-discover-source-empty-title]");
    var sourceEmptyCopy = root.querySelector("[data-discover-source-empty-copy]");
    var sourceButtons = root.querySelectorAll("[data-remix-source]");
    var sourceAnyButton = root.querySelector("[data-remix-source-any]");
    var sourceStatus = root.querySelector("[data-remix-source-status]");
    var mobileFilterToggle = root.querySelector("[data-discover-mobile-filter-toggle]");
    var mobileFilterPanel = root.querySelector("[data-discover-sidebar-panel]");
    var mobileFilterClose = root.querySelector("[data-discover-mobile-filter-close]");
    var card = root.querySelector("[data-discover-card]");
    var title = root.querySelector("[data-discover-title]");
    var artist = root.querySelector("[data-discover-artist]");
    var meta = root.querySelector("[data-discover-meta]");
    var remixButtons = root.querySelectorAll("[data-discover-remix]");
    var remix = remixButtons[0];
    var renderActions = root.querySelector("[data-discover-render-actions]");
    var viewProduct = root.querySelector("[data-discover-view-product]");
    var note = root.querySelector("[data-discover-pool-note]");
    var error = root.querySelector("[data-discover-error]");
    var colourMatch = root.querySelector("[data-colour-match]");
    var matchToggle = root.querySelector("[data-colour-match-toggle]");
    var matchPanel = root.querySelector("[data-colour-match-panel]");
    var matchInput = root.querySelector("[data-colour-match-input]");
    var matchPalette = root.querySelector("[data-colour-match-palette]");
    var matchPreview = root.querySelector("[data-colour-match-preview]");
    var matchSwatches = root.querySelector("[data-colour-match-swatches]");
    var matchAll = root.querySelector("[data-colour-match-all]");
    var matchClear = root.querySelector("[data-colour-match-clear]");
    var matchStatus = root.querySelector("[data-colour-match-status]");
    var visualiserHost = root.matches("[data-frame-visualiser-host]")
      ? root
      : root.querySelector("[data-frame-visualiser-host]");
    var visualiserApi = visualiserHost && window.FrameVisualiser
      ? window.FrameVisualiser.init(visualiserHost)
      : null;
    if (!form || !colour || !category || !orientation || !result || !remix || !viewProduct || !visualiserApi) return;
    var visualiserViews = root.querySelector("[data-frame-visualiser] .views");
    if (renderActions && visualiserViews && visualiserViews.parentNode) {
      visualiserViews.parentNode.insertBefore(renderActions, visualiserViews);
    }

    var mobileFilterQuery = window.matchMedia("(max-width: 680px)");
    if (mobileFilterToggle && mobileFilterPanel && mobileFilterClose) {
      function setMobileFiltersOpen(open) {
        open = !mobileFilterQuery.matches || open;
        mobileFilterPanel.hidden = !open;
        mobileFilterToggle.setAttribute("aria-expanded", open ? "true" : "false");
      }
      mobileFilterToggle.addEventListener("click", function () {
        setMobileFiltersOpen(mobileFilterToggle.getAttribute("aria-expanded") !== "true");
      });
      mobileFilterClose.addEventListener("click", function () {
        setMobileFiltersOpen(false);
        result.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      document.addEventListener("keydown", function (event) {
        if (
          event.key === "Escape" &&
          mobileFilterQuery.matches &&
          mobileFilterToggle.getAttribute("aria-expanded") === "true"
        ) {
          event.preventDefault();
          setMobileFiltersOpen(false);
          mobileFilterToggle.focus();
        }
      });
      if (mobileFilterQuery.addEventListener) {
        mobileFilterQuery.addEventListener("change", function () {
          setMobileFiltersOpen(!mobileFilterQuery.matches);
        });
      } else {
        mobileFilterQuery.addListener(function () {
          setMobileFiltersOpen(!mobileFilterQuery.matches);
        });
      }
      setMobileFiltersOpen(!mobileFilterQuery.matches);
    }

    // Discovery-only presentation state. The shared visualiser and its option
    // handlers remain untouched; this only opens and closes their drawer.
    var drawer = root.querySelector("[data-discover-drawer]");
    var drawerBackdrop = root.querySelector("[data-discover-drawer-backdrop]");
    var drawerTab = root.querySelector(".discover-edge-tab");
    var drawerToggles = root.querySelectorAll("[data-discover-drawer-toggle]");
    var drawerClose = root.querySelector("[data-discover-drawer-close]");
    var lastDrawerOpener = null;
    var drawerRevealTimer = null;
    if (drawer && drawerBackdrop && drawerTab && drawerToggles.length && drawerClose) {
      // Pin chrome to the document so position:fixed stays viewport-relative
      // even if a later ancestor gains transform/filter (e.g. sticky header).
      document.body.appendChild(drawerBackdrop);
      document.body.appendChild(drawer);
      document.body.appendChild(drawerTab);

      function cancelDrawerReveal() {
        if (!drawerRevealTimer) return;
        window.clearTimeout(drawerRevealTimer);
        drawerRevealTimer = null;
      }
      function drawerIsOpen() {
        return root.getAttribute("data-drawer-open") === "true";
      }
      function setDrawerOpen(open, moveFocus) {
        root.setAttribute("data-drawer-open", open ? "true" : "false");
        document.documentElement.classList.toggle("discover-drawer-open", open);
        drawer.setAttribute("aria-hidden", open ? "false" : "true");
        if (open) drawer.removeAttribute("inert");
        else drawer.setAttribute("inert", "");
        drawerToggles.forEach(function (button) {
          button.setAttribute("aria-expanded", open ? "true" : "false");
        });
        drawerBackdrop.hidden = !open;
        document.documentElement.classList.toggle(
          "discover-drawer-lock",
          open && window.matchMedia("(max-width: 900px)").matches
        );
        if (!moveFocus) return;
        if (open) drawer.focus();
        else if (lastDrawerOpener) lastDrawerOpener.focus();
      }
      drawerToggles.forEach(function (button) {
        button.addEventListener("click", function () {
          cancelDrawerReveal();
          var willOpen = !drawerIsOpen();
          if (willOpen) lastDrawerOpener = button;
          setDrawerOpen(willOpen, true);
        });
      });
      drawerClose.addEventListener("click", function () {
        cancelDrawerReveal();
        setDrawerOpen(false, true);
      });
      drawerBackdrop.addEventListener("click", function () {
        cancelDrawerReveal();
        setDrawerOpen(false, true);
      });
      drawer.addEventListener("pointerdown", cancelDrawerReveal);
      drawer.addEventListener("focusin", cancelDrawerReveal);
      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && drawerIsOpen()) {
          cancelDrawerReveal();
          event.preventDefault();
          setDrawerOpen(false, true);
          return;
        }
        if (event.key === "Tab" && drawerIsOpen()) {
          var focusable = drawer.querySelectorAll(
            'button:not([disabled]),a[href],select:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
          );
          if (!focusable.length) return;
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    }

    var params = new URLSearchParams(location.search);
    var usesSearchPreviewRoute = params.get("view") === "discover";
    var initial = {
      colour: params.get("dc") || "",
      category: params.get("dcat") || "all",
      orientation: params.get("do") || "",
      mood: String(params.get("dm") || "").trim().toLowerCase()
    };
    var seedHandle = String(params.get("product") || "").trim().toLowerCase();
    var seedLookPending = !!seedHandle;
    var affinitySeed = null;
    var seededLookOptions = (function readSeedLook() {
      function param() {
        for (var i = 0; i < arguments.length; i++) {
          var value = params.get(arguments[i]);
          if (value != null && String(value).trim()) return String(value).trim();
        }
        return "";
      }
      var options = {
        frameStyle: param("frame", "df"),
        colour: param("colour", "color", "dcolour"),
        size: param("size", "dsize"),
        mount: param("mount", "dmount"),
        mountColour: param("mountcolour", "mount_colour", "mount-colour", "dmountcolour"),
        scene: param("scene", "dscene")
      };
      var hasLook = Object.keys(options).some(function (key) { return !!options[key]; });
      return hasLook ? options : null;
    })();
    var pool = [];
    var poolSource = "category";
    var total = 0;
    var capped = false;
    var lastId = null;
    var requestNumber = 0;
    var swapNumber = 0;
    var controller = null;
    var currentProduct = null;
    var matchObjectUrl = "";
    var extractedPalette = [];
    var activeColourMatch = null;
    var remixSource = "any";
    var seedRemixSource = String(params.get("dsource") || params.get("remix") || "").trim().toLowerCase();
    if (seedRemixSource !== "liked" && seedRemixSource !== "loved") seedRemixSource = "";
    // Seed URLs may include orientation labels that only resolve after the
    // first filter payload arrives. Colour now uses browse hubs, so it is
    // resolved before the first pool request.
    var seedFilterBootstrap = !!String(params.get("do") || "").trim();
    var shareLookButton = root.querySelector("[data-discover-share-look]");
    var shareLookStatus = root.querySelector("[data-discover-share-status]");
    var priceEl = root.querySelector("[data-discover-price]");
    var moodStrip = root.querySelector("[data-discover-mood]");
    var selectedMood = initial.mood && /^(calm|quiet|soft|warm|vibrant|energetic|moody|dramatic|atmospheric|nostalgic|contemplative|minimal)$/.test(initial.mood)
      ? initial.mood
      : "";
    var pricingCache = {};
    var pricingRequest = 0;

    function productReactionId(product) {
      return String(product && (product.handle || product.id) || "");
    }

    function undislikedProducts() {
      var reactions = window.TheArtfolioReactions;
      if (!reactions) return pool.slice();
      return pool.filter(function (product) {
        return reactions.getReaction(productReactionId(product)) !== "dislike";
      });
    }

    function sourceIds(source) {
      var ids = {};
      if (source === "liked" && window.TheArtfolioReactions) {
        var reactions = window.TheArtfolioReactions.getAllReactions();
        Object.keys(reactions).forEach(function (id) {
          if (reactions[id] === "like") ids[id] = true;
        });
      } else if (source === "loved" && window.TheArtfolioFavourites) {
        window.TheArtfolioFavourites.getFavourites().forEach(function (favourite) {
          try {
            var pathname = new URL(favourite.url, location.origin).pathname;
            var match = pathname.match(/\/products\/([^/?]+)/);
            if (match) ids[decodeURIComponent(match[1])] = true;
          } catch (error) {
            // Ignore malformed legacy favourites.
          }
        });
      }
      return ids;
    }

    function productMoodKeys(product) {
      return productMoodList(product);
    }

    function availableProducts() {
      var available = undislikedProducts();
      if (selectedMood) {
        available = available.filter(function (product) {
          return productMoodKeys(product).indexOf(selectedMood) !== -1;
        });
      }
      if (poolSource === "colour") {
        var categoryHandle = category.value || initial.category || "all";
        available = available.filter(function (product) {
          return productMatchesCategory(product, categoryHandle);
        });
      }
      if (remixSource === "any") return available;
      var allowed = sourceIds(remixSource);
      return available.filter(function (product) {
        return !!allowed[productReactionId(product)];
      });
    }

    function productMatchesCategory(product, categoryHandle) {
      var wanted = handleize(categoryHandle);
      if (!wanted || wanted === "all") return true;
      var genre = handleize(product.genre || product.type || "");
      if (genre && (genre === wanted || genre.indexOf(wanted) !== -1 || wanted.indexOf(genre) !== -1)) {
        return true;
      }
      var tags = product.tags || [];
      if (!Array.isArray(tags)) tags = String(tags).split(",");
      return tags.some(function (tag) {
        return handleize(tag) === wanted;
      });
    }

    function showEmptyReason(reason) {
      if (emptyDefault) emptyDefault.hidden = reason !== "normal";
      if (reactionEmpty) reactionEmpty.hidden = reason !== "disliked";
      if (sourceEmpty) sourceEmpty.hidden = reason !== "source";
    }

    function sourceCounts() {
      var liked = 0;
      var loved = 0;
      if (window.TheArtfolioReactions) {
        var reactions = window.TheArtfolioReactions.getAllReactions();
        Object.keys(reactions).forEach(function (id) {
          if (reactions[id] === "like") liked += 1;
        });
      }
      if (window.TheArtfolioFavourites) {
        loved = window.TheArtfolioFavourites.getFavourites().length;
      }
      return { liked: liked, loved: loved };
    }

    function loadSourcePool(source, done) {
      var handles = Object.keys(sourceIds(source));
      if (!handles.length) {
        done([]);
        return;
      }
      var pending = handles.slice(0, POOL_LIMIT);
      var remaining = pending.length;
      var products = [];
      pending.forEach(function (handle) {
        fetchSeedProduct(handle).then(function (product) {
          if (product) products.push(product);
        }).catch(function () {
          // Skip deleted / unavailable handles.
        }).then(function () {
          remaining -= 1;
          if (remaining <= 0) done(products);
        });
      });
    }

    function updateSourceControl() {
      var counts = sourceCounts();
      sourceButtons.forEach(function (button) {
        var source = button.dataset.remixSource;
        button.setAttribute(
          "aria-pressed",
          source === remixSource ? "true" : "false"
        );
        if (source === "liked" || source === "loved") {
          var count = counts[source] || 0;
          var countEl = button.querySelector("[data-remix-count]");
          if (countEl) countEl.textContent = String(count);
          button.setAttribute("data-remix-empty", count === 0 ? "true" : "false");
          button.setAttribute(
            "aria-label",
            source === "liked"
              ? (count === 1 ? "Liked, 1 saved" : "Liked, " + count + " saved")
              : (count === 1 ? "Loved, 1 saved" : "Loved, " + count + " saved")
          );
        }
      });
      if (sourceStatus) {
        if (remixSource === "any") {
          sourceStatus.textContent = "Remixing from any matching artwork.";
        } else if (remixSource === "liked") {
          sourceStatus.textContent = counts.liked
            ? "Remixing from " + counts.liked + (counts.liked === 1 ? " liked artwork." : " liked artworks.")
            : "No liked artworks saved yet.";
        } else {
          sourceStatus.textContent = counts.loved
            ? "Remixing from " + counts.loved + (counts.loved === 1 ? " loved look." : " loved looks.")
            : "No loved looks saved yet.";
        }
      }
    }

    function setRemixSource(source) {
      remixSource = source === "liked" || source === "loved" ? source : "any";
      updateSourceControl();
      refreshTasteNote();
      if (remixSource === "any") {
        loadPool();
        return;
      }
      // Liked / Loved remix from the saved set itself — not from the current
      // filtered Discover sample (which often contains none of those works).
      setState("loading");
      note.textContent = "";
      loadSourcePool(remixSource, function (products) {
        pool = products;
        total = products.length;
        capped = false;
        choose();
        refreshTasteNote();
      });
    }

    if (category.querySelector('option[value="' + cssEscape(initial.category) + '"]')) {
      category.value = initial.category;
    }

    function cssEscape(value) {
      if (window.CSS && CSS.escape) return CSS.escape(value);
      return String(value).replace(/["\\]/g, "\\$&");
    }

    function selectedParam(select, fallback) {
      var option = select.options[select.selectedIndex];
      return option && option.dataset.param ? option.dataset.param : fallback;
    }

    function optionLabel(option) {
      if (!option) return "";
      if (option.dataset && option.dataset.label) return option.dataset.label;
      return String(option.textContent || "").replace(/\s+\(\d+\)\s*$/, "").trim();
    }

    // Seed links often pass colour labels ("Blue") or lowercase orientation
    // ("portrait"). Shopify filters need taxonomy GIDs / title-case values —
    // unmatched seeds must not be sent, or the pool collapses to zero.
    function resolveSelectFilter(select, requested) {
      if (!select || requested == null || requested === "") return "";
      var req = String(requested).trim();
      if (!req) return "";
      var reqKey = colourKey(req);
      var matched = "";
      Array.prototype.forEach.call(select.options, function (option) {
        if (!option.value || matched) return;
        if (option.value === req) {
          matched = option.value;
          return;
        }
        if (colourKey(option.value) === reqKey) {
          matched = option.value;
          return;
        }
        if (colourKey(optionLabel(option)) === reqKey) matched = option.value;
      });
      return matched;
    }

    function canRemix() {
      var available = availableProducts();
      if (available.length > 1) return true;
      if (available.length === 1 && currentProduct && String(available[0].id) !== String(currentProduct.id)) {
        return true;
      }
      return pool.length > 1;
    }

    function setState(state) {
      result.setAttribute("aria-busy", state === "loading" ? "true" : "false");
      loading.hidden = state !== "loading";
      empty.hidden = state !== "empty";
      card.hidden = state !== "card";
      error.hidden = state !== "error";
      remixButtons.forEach(function (button) {
        button.disabled = state !== "card" || !canRemix();
      });
      viewProduct.setAttribute("aria-disabled", state === "card" ? "false" : "true");
      if (state !== "card") viewProduct.removeAttribute("href");
    }

    function browseColourItems() {
      var node = root.querySelector("[data-browse-colour-index]");
      if (!node) return [];
      try {
        var parsed = JSON.parse(node.textContent || "[]");
        if (!Array.isArray(parsed)) return [];
        return parsed.map(function (item) {
          return {
            value: item.value,
            label: item.label,
            param: "browse-collection",
            count: item.count || 0,
            handle: item.handle || colourCollectionHandle(item.value)
          };
        });
      } catch (error) {
        return [];
      }
    }

    function colourHandleForValue(value) {
      var key = colourKey(value);
      if (!key) return "";
      var items = browseColourItems();
      for (var i = 0; i < items.length; i += 1) {
        if (colourKey(items[i].value) === key || colourKey(items[i].label) === key) {
          return items[i].handle;
        }
      }
      return colourCollectionHandle(key);
    }

    function populateSelect(select, items, anyLabel, requested) {
      if (!items || !items.length) {
        select.disabled = false;
        return;
      }
      var html = '<option value="">' + anyLabel + "</option>";
      items.forEach(function (item) {
        html += '<option value="' + escapeHtml(item.value) + '" data-param="' +
          escapeHtml(item.param) + '" data-label="' + escapeHtml(item.label) + '">' +
          escapeHtml(item.label) + " (" +
          Number(item.count || 0) + ")</option>";
      });
      select.innerHTML = html;
      var resolved = resolveSelectFilter(select, requested != null ? requested : select.value);
      select.value = resolved || "";
      select.disabled = false;
    }

    function escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    function colourOptionForTag(tag) {
      return Array.prototype.find.call(colour.options, function (option) {
        return colourKey(option.dataset.label || option.textContent.replace(/\s+\(\d+\)\s*$/, "")) === colourKey(tag);
      }) || null;
    }

    function colourMatchFilterEntries() {
      if (!activeColourMatch) return [];
      return activeColourMatch.tags.map(colourOptionForTag).filter(Boolean).map(function (option) {
        return {
          param: option.dataset.param || "filter.v.t.shopify.color-pattern",
          value: option.value
        };
      }).filter(function (entry, index, entries) {
        return entries.findIndex(function (other) {
          return other.param === entry.param && other.value === entry.value;
        }) === index;
      });
    }

    function revokeMatchObjectUrl() {
      if (!matchObjectUrl) return;
      URL.revokeObjectURL(matchObjectUrl);
      matchObjectUrl = "";
    }

    function rgbHex(r, g, b) {
      return "#" + [r, g, b].map(function (value) {
        return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0");
      }).join("");
    }

    function extractDominantColours(image) {
      var maxSide = 96;
      var scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
      var width = Math.max(1, Math.round(image.naturalWidth * scale));
      var height = Math.max(1, Math.round(image.naturalHeight * scale));
      var canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      var context = canvas.getContext("2d", { willReadFrequently: true });
      context.drawImage(image, 0, 0, width, height);
      var pixels = context.getImageData(0, 0, width, height).data;
      var buckets = {};
      for (var i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] < 180) continue;
        var r = pixels[i];
        var g = pixels[i + 1];
        var b = pixels[i + 2];
        var key = (r >> 5) + "-" + (g >> 5) + "-" + (b >> 5);
        var bucket = buckets[key] || { count: 0, r: 0, g: 0, b: 0 };
        bucket.count += 1;
        bucket.r += r;
        bucket.g += g;
        bucket.b += b;
        buckets[key] = bucket;
      }
      var candidates = Object.keys(buckets).map(function (key) {
        var bucket = buckets[key];
        return {
          count: bucket.count,
          r: bucket.r / bucket.count,
          g: bucket.g / bucket.count,
          b: bucket.b / bucket.count
        };
      }).sort(function (a, b) { return b.count - a.count; }).slice(0, 40);

      var selected = [];
      while (selected.length < 5 && candidates.length) {
        var bestIndex = 0;
        var bestScore = -1;
        candidates.forEach(function (candidate, index) {
          var separation = selected.length ? Math.min.apply(null, selected.map(function (chosen) {
            return Math.pow(candidate.r - chosen.r, 2) +
              Math.pow(candidate.g - chosen.g, 2) +
              Math.pow(candidate.b - chosen.b, 2);
          })) : 65025;
          var score = candidate.count * Math.max(separation, 625);
          if (score > bestScore) {
            bestScore = score;
            bestIndex = index;
          }
        });
        selected.push(candidates.splice(bestIndex, 1)[0]);
      }
      return selected.map(function (colour) {
        var hex = rgbHex(colour.r, colour.g, colour.b);
        return { hex: hex, tag: extractedColourToTag(hex) };
      });
    }

    function renderExtractedPalette() {
      if (!matchPalette || !matchSwatches) return;
      matchSwatches.textContent = "";
      extractedPalette.forEach(function (colourData, index) {
        var button = document.createElement("button");
        button.type = "button";
        button.className = "discover-match-swatch";
        button.style.setProperty("--match-colour", colourData.hex);
        button.dataset.matchIndex = String(index);
        button.setAttribute("aria-label", colourData.tag + " - use extracted colour " + colourData.hex);
        button.setAttribute("aria-pressed", "false");
        var chip = document.createElement("span");
        chip.setAttribute("aria-hidden", "true");
        var name = document.createElement("small");
        name.textContent = colourData.tag;
        button.appendChild(chip);
        button.appendChild(name);
        matchSwatches.appendChild(button);
      });
      matchPalette.hidden = false;
    }

    function updateMatchSelection() {
      if (!matchSwatches || !matchAll) return;
      matchSwatches.querySelectorAll("[data-match-index]").forEach(function (button) {
        var selected = activeColourMatch && activeColourMatch.mode === "single" &&
          Number(button.dataset.matchIndex) === activeColourMatch.index;
        button.setAttribute("aria-pressed", selected ? "true" : "false");
      });
      matchAll.setAttribute(
        "aria-pressed",
        activeColourMatch && activeColourMatch.mode === "all" ? "true" : "false"
      );
    }

    function activateColourMatch(mode, index) {
      if (!extractedPalette.length) return;
      var selected = mode === "single" ? [extractedPalette[index]] : extractedPalette;
      activeColourMatch = {
        mode: mode,
        index: mode === "single" ? index : null,
        hexes: selected.map(function (entry) { return entry.hex; }),
        tags: selected.map(function (entry) { return entry.tag; }).filter(function (tag, tagIndex, tags) {
          return tags.indexOf(tag) === tagIndex;
        })
      };
      colour.value = "";
      updateUrl();
      updateMatchSelection();
      matchStatus.textContent = mode === "single"
        ? "Matching: " + activeColourMatch.tags[0]
        : "Best matches: " + activeColourMatch.tags.join(", ");
      loadPool();
    }

    function clearActiveColourMatch(reload) {
      activeColourMatch = null;
      updateMatchSelection();
      if (matchStatus && extractedPalette.length) matchStatus.textContent = "Choose a colour, or use all.";
      if (reload) loadPool();
    }

    function clearUploadedMatch(reload) {
      revokeMatchObjectUrl();
      extractedPalette = [];
      activeColourMatch = null;
      if (matchInput) matchInput.value = "";
      if (matchPreview) matchPreview.removeAttribute("src");
      if (matchPalette) matchPalette.hidden = true;
      if (matchSwatches) matchSwatches.textContent = "";
      if (matchStatus) matchStatus.textContent = "";
      if (reload) loadPool();
    }

    function updateUrl() {
      var next = new URL(location.href);
      if (usesSearchPreviewRoute) next.searchParams.set("view", "discover");
      else next.searchParams.delete("view");
      if (colour.value) {
        next.searchParams.set("dc", optionLabel(colour.options[colour.selectedIndex]) || colour.value);
      } else next.searchParams.delete("dc");
      if (category.value && category.value !== "all") next.searchParams.set("dcat", category.value);
      else next.searchParams.delete("dcat");
      if (orientation.value) next.searchParams.set("do", orientation.value);
      else next.searchParams.delete("do");
      if (selectedMood) next.searchParams.set("dm", selectedMood);
      else next.searchParams.delete("dm");
      if (currentProduct && currentProduct.handle) {
        next.searchParams.set("product", currentProduct.handle);
      } else if (seedHandle && seedLookPending) {
        next.searchParams.set("product", seedHandle);
      } else {
        next.searchParams.delete("product");
      }
      var options = visualiserApi.getOptions();
      var lookPairs = [
        ["frame", options.frameStyle],
        ["colour", options.colour],
        ["size", options.size],
        ["mount", options.mount],
        ["mountcolour", options.mountColour],
        ["scene", options.scene]
      ];
      lookPairs.forEach(function (pair) {
        if (pair[1] != null && String(pair[1]).trim()) next.searchParams.set(pair[0], String(pair[1]));
        else next.searchParams.delete(pair[0]);
      });
      ["df", "dcolour", "dsize", "dmount", "dmountcolour", "dscene", "look"].forEach(function (key) {
        next.searchParams.delete(key);
      });
      history.replaceState(null, "", next.pathname + next.search + next.hash);
    }

    function applySeededLook() {
      if (!seededLookOptions || !visualiserApi.setOptions) return;
      visualiserApi.setOptions(seededLookOptions);
      seededLookOptions = null;
    }

    function formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === "function") {
        return window.theme.formatMoney(cents);
      }
      var amount = Number(cents);
      if (!isFinite(amount)) return "";
      return "£" + (amount / 100).toFixed(2);
    }

    function optionLabelName(option) {
      if (typeof option === "string") return option;
      return option && option.name ? String(option.name) : "";
    }

    function looksLikeOption(name, words) {
      name = String(name || "").toLowerCase();
      return words.some(function (word) { return name.indexOf(word) > -1; });
    }

    function sizeTier(value) {
      var key = String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      if (key.indexOf("diptych") > -1) return "diptych";
      if (key.indexOf("triptych") > -1) return "triptych";
      if (key === "xl" || key.indexOf("extralarge") > -1 || key.indexOf("xlarge") > -1) return "extralarge";
      if (key.indexOf("small") > -1) return "small";
      if (key.indexOf("medium") > -1) return "medium";
      if (key.indexOf("large") > -1) return "large";
      return key;
    }

    function frameStyleKey(value) {
      var v = String(value || "").toLowerCase();
      if (/deep|wide\s*box|widebox/.test(v)) return "deep";
      if (/swoop/.test(v)) return "swoop";
      if (/venice/.test(v)) return "venice";
      if (/classic|standard/.test(v)) return "classic";
      return v.replace(/[^a-z0-9]+/g, "");
    }

    function isFrameStyleValue(value) {
      return /classic|swoop|venice|deep|standard|wide\s*box|widebox/.test(String(value || "").toLowerCase());
    }

    function optionValuesMatch(wanted, actual, kind) {
      if (wanted == null || actual == null) return false;
      var a = String(wanted).trim();
      var b = String(actual).trim();
      if (!a || !b) return false;
      if (a.toLowerCase() === b.toLowerCase()) return true;
      if (kind === "size") return sizeTier(a) === sizeTier(b);
      if (kind === "frametype") return frameStyleKey(a) === frameStyleKey(b);
      if (kind === "mount") {
        var am = /no|none|unmounted/.test(a.toLowerCase());
        var bm = /no|none|unmounted/.test(b.toLowerCase());
        return am === bm;
      }
      return false;
    }

    function findConfiguredVariant(pricing, look) {
      if (!pricing || !pricing.variants || !pricing.variants.length) return null;
      var optionNames = (pricing.options || []).map(optionLabelName);
      var optionValueLists = (pricing.options || []).map(function (option) {
        if (typeof option === "string") return [];
        return Array.isArray(option.values) ? option.values : [];
      });
      if (!optionNames.length || (optionNames.length === 1 && /^title$/i.test(optionNames[0]))) {
        return pricing.variants[0];
      }
      return pricing.variants.find(function (variant) {
        var opts = variant.options || [variant.option1, variant.option2, variant.option3].filter(function (v) {
          return v != null && v !== "";
        });
        return optionNames.every(function (name, index) {
          var probe = (optionValueLists[index] || []).concat([opts[index]]).join(" ");
          var kind = looksLikeOption(name, ["size", "dimension"]) ? "size"
            : looksLikeOption(name, ["mount"]) ? "mount"
            : looksLikeOption(name, ["frame"]) && isFrameStyleValue(probe) ? "frametype"
            : looksLikeOption(name, ["frame"]) ? "frame"
            : "other";
          var wanted = kind === "size" ? look.size
            : kind === "mount" ? look.mount
            : kind === "frametype" ? look.frameStyle
            : kind === "frame" ? look.colour
            : null;
          if (wanted == null || wanted === "") return true;
          return optionValuesMatch(wanted, opts[index], kind === "frame" ? "other" : kind);
        });
      }) || null;
    }

    function updateDiscoverPrice(product, pricing) {
      if (!priceEl) return;
      var look = visualiserApi && visualiserApi.getOptions ? visualiserApi.getOptions() : {};
      var matched = pricing ? findConfiguredVariant(pricing, look) : null;
      if (matched && matched.price != null) {
        priceEl.hidden = false;
        priceEl.textContent = formatMoney(matched.price);
        return;
      }
      var cents = product && (product.priceMin != null ? product.priceMin : product.price);
      if (cents == null || cents === "") {
        priceEl.hidden = true;
        priceEl.textContent = "";
        return;
      }
      priceEl.hidden = false;
      priceEl.textContent = "From " + formatMoney(cents);
    }

    function attachPricing(product, raw) {
      if (!product || !raw) return product;
      var options = raw.options || [];
      product.pricing = {
        options: options,
        variants: Array.isArray(raw.variants) ? raw.variants : []
      };
      if (raw.price_min != null) product.priceMin = raw.price_min;
      else if (raw.price != null && product.priceMin == null) product.priceMin = raw.price;
      return product;
    }

    function ensureProductPricing(product) {
      if (!product || !product.handle) return Promise.resolve(null);
      var handle = String(product.handle).toLowerCase();
      if (product.pricing && product.pricing.variants) {
        pricingCache[handle] = product.pricing;
        return Promise.resolve(product.pricing);
      }
      if (pricingCache[handle]) {
        product.pricing = pricingCache[handle];
        return Promise.resolve(product.pricing);
      }
      return fetch("/products/" + encodeURIComponent(product.handle) + ".js", {
        credentials: "same-origin"
      }).then(function (response) {
        if (!response.ok) throw new Error("Pricing product missing");
        return response.json();
      }).then(function (raw) {
        attachPricing(product, raw);
        pricingCache[handle] = product.pricing;
        return product.pricing;
      }).catch(function () {
        return null;
      });
    }

    function refreshDiscoverPrice() {
      if (!currentProduct || !priceEl) return;
      var thisRequest = ++pricingRequest;
      ensureProductPricing(currentProduct).then(function (pricing) {
        if (thisRequest !== pricingRequest) return;
        if (currentProduct) updateDiscoverPrice(currentProduct, pricing);
      });
    }

    function fetchSeedProduct(handle) {
      return Promise.all([
        fetch("/products/" + encodeURIComponent(handle) + ".js", {
          credentials: "same-origin"
        }).then(function (response) {
          if (!response.ok) throw new Error("Seed product missing");
          return response.json();
        }),
        fetch("/products/" + encodeURIComponent(handle) + "?view=browse-seed", {
          credentials: "same-origin"
        }).then(function (response) {
          return response.ok ? response.json() : null;
        }).catch(function () { return null; })
      ]).then(function (results) {
        var raw = results[0];
        var meta = results[1] || {};
        var image = "";
        if (raw.featured_image) {
          image = typeof raw.featured_image === "string"
            ? raw.featured_image
            : (raw.featured_image.src || "");
        } else if (raw.images && raw.images[0]) {
          image = typeof raw.images[0] === "string" ? raw.images[0] : (raw.images[0].src || "");
        }
        if (image.indexOf("//") === 0) image = "https:" + image;
        var moods = Array.isArray(meta.moods) ? meta.moods : [];
        var colours = Array.isArray(meta.colours) ? meta.colours.map(colourKey).filter(Boolean) : [];
        var product = {
          id: raw.id,
          handle: raw.handle,
          title: raw.title,
          url: "/products/" + encodeURIComponent(raw.handle),
          artist: raw.vendor || "",
          orientation: meta.orientation || "",
          paletteClass: "a-slate",
          colours: colours,
          moods: moods,
          genre: meta.genre || "",
          image: image,
          imageHighRes: image,
          imageAlt: raw.title || "Artwork",
          priceMin: raw.price_min != null ? raw.price_min : raw.price
        };
        attachPricing(product, raw);
        pricingCache[String(raw.handle || "").toLowerCase()] = product.pricing;
        return product;
      });
    }

    function endpoint() {
      var colourValue = colour.value || resolveSelectFilter(colour, initial.colour);
      var orientationValue = orientation.value || resolveSelectFilter(orientation, initial.orientation);
      var categoryHandle = category.value || initial.category || "all";
      var colourHandle = "";
      if (activeColourMatch && activeColourMatch.tags && activeColourMatch.tags[0]) {
        colourHandle = colourHandleForValue(activeColourMatch.tags[0]);
      } else if (colourValue) {
        colourHandle = colourHandleForValue(colourValue);
      }
      poolSource = colourHandle ? "colour" : "category";
      var handle = colourHandle || categoryHandle || "all";
      var base = (root.getAttribute("data-pool-root") || "/collections").replace(/\/$/, "");
      var url = new URL(base + "/" + encodeURIComponent(handle), location.origin);
      url.searchParams.set("view", "discover-pool");

      if (orientationValue) {
        url.searchParams.set(
          selectedParam(orientation, "filter.p.m.custom.orientation"),
          orientationValue
        );
      }
      return url.toString();
    }

    function ensureSeedInPool(done) {
      if (!seedHandle) {
        done();
        return;
      }
      var existing = pool.find(function (product) {
        return String(product.handle || "").toLowerCase() === seedHandle;
      });
      if (existing) {
        done(existing);
        return;
      }
      fetchSeedProduct(seedHandle).then(function (seedProduct) {
        if (seedProduct) {
          affinitySeed = seedProduct;
          pool = [seedProduct].concat(pool.filter(function (product) {
            return String(product.id) !== String(seedProduct.id);
          }));
          if (!total) total = pool.length;
        }
        done(seedProduct || null);
      }).catch(function () {
        done(null);
      });
    }

    function choose() {
      if (!pool.length) {
        if (seedLookPending && seedHandle) {
          ensureSeedInPool(function (seedProduct) {
            if (!seedProduct && !pool.length) {
              note.textContent = "";
              showEmptyReason("normal");
              setState("empty");
              return;
            }
            choose();
          });
          return;
        }
        note.textContent = "";
        showEmptyReason("normal");
        if (activeColourMatch && matchStatus) {
          matchStatus.textContent = "No tagged colour matches in these filters. Try another swatch or clear a filter.";
        }
        setState("empty");
        return;
      }
      var undisliked = undislikedProducts();
      if (!undisliked.length) {
        note.textContent = "";
        showEmptyReason("disliked");
        setState("empty");
        return;
      }
      var available = availableProducts();
      if (!available.length) {
        if (remixSource !== "any") {
          var hasAnySaved = Object.keys(sourceIds(remixSource)).length > 0;
          if (sourceEmptyTitle) {
            sourceEmptyTitle.textContent = remixSource === "liked"
              ? (hasAnySaved ? "Your liked works couldn’t be shown right now." : "You haven’t liked any works yet.")
              : (hasAnySaved ? "Your loved looks couldn’t be shown right now." : "You haven’t loved any works yet.");
          }
          if (sourceEmptyCopy) {
            sourceEmptyCopy.textContent = hasAnySaved
              ? "Try Remix from Any, or save a few more artworks and try again."
              : (remixSource === "liked"
                ? "Like a few artworks, or switch Remix back to Any."
                : "Save a few looks with the heart, or switch Remix back to Any.");
          }
          note.textContent = "";
          showEmptyReason("source");
          setState("empty");
          return;
        }
        note.textContent = "";
        showEmptyReason("normal");
        setState("empty");
        return;
      }
      showEmptyReason(null);
      var candidates = available;
      if (activeColourMatch && activeColourMatch.mode === "all" && available[0]._colourMatchScore) {
        var bestScore = available[0]._colourMatchScore;
        candidates = available.filter(function (product) {
          return product._colourMatchScore === bestScore;
        });
      }
      if (available.length > 1 && lastId != null) {
        var withoutRepeat = candidates.filter(function (product) {
          return String(product.id) !== String(lastId);
        });
        if (withoutRepeat.length) candidates = withoutRepeat;
        else candidates = available.filter(function (product) {
          return String(product.id) !== String(lastId);
        });
      }
      var picked = null;
      if (seedLookPending && seedHandle) {
        picked = available.find(function (product) {
          return String(product.handle || "").toLowerCase() === seedHandle;
        }) || pool.find(function (product) {
          return String(product.handle || "").toLowerCase() === seedHandle;
        }) || null;
        if (picked) {
          seedLookPending = false;
          affinitySeed = picked;
          lastId = picked.id;
          renderCard(picked);
          return;
        }
        seedLookPending = false;
        ensureSeedInPool(function (seedProduct) {
          if (seedProduct) {
            affinitySeed = seedProduct;
            lastId = seedProduct.id;
            renderCard(seedProduct);
            return;
          }
          picked = pickAffinedCandidate(candidates, affinitySeed, lastId) || available[0];
          lastId = picked.id;
          renderCard(picked);
        });
        return;
      }
      picked = pickAffinedCandidate(candidates, affinitySeed, lastId) || available[0];
      lastId = picked.id;
      renderCard(picked);
    }

    function poolNote() {
      var availableCount = availableProducts().length;
      if (remixSource !== "any") {
        var sourceLabel = remixSource === "liked" ? "liked" : "loved";
        return availableCount + " " + sourceLabel + (availableCount === 1 ? " work" : " works") +
          (activeColourMatch ? " · palette matched" : "") +
          (capped ? " · within this fast sample" : "");
      }
      if (activeColourMatch) {
        return availableCount + (availableCount === 1 ? " palette match" : " palette matches") +
          (affinitySeed ? " · ranked near your seed" : "") +
          (capped ? " · ranked from a fast sample of " + POOL_LIMIT : "");
      }
      if (pool.length === 1) return "One matching work";
      return (availableCount < pool.length ? availableCount : total) + " matching works" +
        (affinitySeed ? " · ranked near your seed" : "") +
        (capped ? " · remixing from a fast sample of " + POOL_LIMIT : "");
    }

    function productLookUrl() {
      if (!currentProduct || !currentProduct.handle) return "";
      var options = visualiserApi.getOptions();
      var base = new URL(
        currentProduct.url || ("/products/" + encodeURIComponent(currentProduct.handle)),
        location.origin
      );
      var values = [
        ["frame", options.frameStyle],
        ["colour", options.colour],
        ["size", options.size],
        ["mount", options.mount],
        ["mountcolour", options.mountColour],
        ["scene", options.scene]
      ];
      base.search = values
        .filter(function (pair) { return pair[1] != null && String(pair[1]).trim() !== ""; })
        .map(function (pair) {
          return encodeURIComponent(pair[0]) + "=" + encodeURIComponent(pair[1]);
        })
        .join("&");
      return base.pathname + base.search;
    }

    function updateViewProductLink() {
      var url = productLookUrl();
      if (url) {
        viewProduct.href = url;
        viewProduct.removeAttribute("aria-disabled");
        if (shareLookButton) shareLookButton.disabled = false;
      } else {
        viewProduct.removeAttribute("href");
        viewProduct.setAttribute("aria-disabled", "true");
        if (shareLookButton) shareLookButton.disabled = true;
      }
      return url;
    }

    function copyLookLink() {
      var path = productLookUrl();
      if (!path) return Promise.resolve(false);
      var url = new URL(path, location.origin).href;
      function fallbackCopy() {
        var field = document.createElement("textarea");
        field.value = url;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.left = "-9999px";
        document.body.appendChild(field);
        field.select();
        var copied = false;
        try { copied = document.execCommand("copy"); } catch (error) {}
        field.remove();
        return copied;
      }
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        return navigator.clipboard.writeText(url).then(function () { return true; }).catch(function () {
          return fallbackCopy();
        });
      }
      return Promise.resolve(fallbackCopy());
    }

    function applyArtwork(product, thisSwap) {
      if (thisSwap !== swapNumber) return;
      if (!PRESERVE_OPTIONS_ON_REMIX && currentProduct) {
        visualiserApi.setOptions({
          frameStyle: "Standard", colour: "Oak", size: "Large",
          mount: "Mounted", mountColour: "Fresh Linen", scene: "artwork"
        });
      }
      currentProduct = product;
      root.dataset.discoverCurrentHandle = product.handle || "";
      root.dataset.discoverCurrentId = product.id || "";
      title.textContent = product.title || "Untitled";
      artist.textContent = product.artist ? "by " + product.artist : "";
      artist.hidden = !product.artist;
      meta.textContent = product.orientation || "Artwork";
      visualiserApi.setArtwork({
        image: product.image,
        highRes: product.imageHighRes || product.image,
        alt: product.imageAlt || product.title || "Artwork",
        title: product.title,
        orientation: product.orientation,
        paletteClass: product.paletteClass
      });
      if (seededLookOptions) applySeededLook();
      note.textContent = poolNote();
      setState("card");
      updateViewProductLink();
      updateDiscoverPrice(product, product.pricing || null);
      refreshDiscoverPrice();
      updateUrl();
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { card.classList.remove("is-swapping"); });
      });
      root.dispatchEvent(new CustomEvent("discover:pick", {
        detail: { product: product, options: visualiserApi.getOptions() }
      }));
    }

    function renderCard(product) {
      swapNumber += 1;
      var thisSwap = swapNumber;
      card.classList.add("is-swapping");
      result.setAttribute("aria-busy", "true");
      remixButtons.forEach(function (button) { button.disabled = true; });

      if (!product.image) {
        applyArtwork(product, thisSwap);
        return;
      }
      var preload = new Image();
      preload.onload = preload.onerror = function () { applyArtwork(product, thisSwap); };
      preload.src = product.image;
    }

    function loadPool() {
      if (remixSource === "liked" || remixSource === "loved") {
        setState("loading");
        note.textContent = "";
        loadSourcePool(remixSource, function (products) {
          pool = products;
          total = products.length;
          capped = false;
          choose();
          refreshTasteNote();
        });
        return;
      }

      requestNumber += 1;
      var thisRequest = requestNumber;
      if (controller) controller.abort();
      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      setState("loading");
      note.textContent = "";

      fetch(endpoint(), {
        credentials: "same-origin",
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error("Pool request failed: " + response.status);
          return response.json();
        })
        .then(function (data) {
          if (thisRequest !== requestNumber) return;
          var incomingProducts = Array.isArray(data.products) ? data.products : [];
          pool = activeColourMatch ? findMatches(activeColourMatch, incomingProducts) : incomingProducts;
          total = Number(data.total || pool.length);
          capped = !!data.capped;

          populateSelect(
            orientation,
            data.filters && data.filters.orientations,
            "Any orientation",
            orientation.value || initial.orientation
          );
          initial.colour = "";
          initial.orientation = "";
          updateUrl();
          if (seedFilterBootstrap) {
            seedFilterBootstrap = false;
            if (colour.value || orientation.value) {
              loadPool();
              return;
            }
          }
          if (seedLookPending && seedHandle) {
            ensureSeedInPool(function () { choose(); refreshTasteNote(); });
          } else {
            choose();
            refreshTasteNote();
          }
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          if (thisRequest !== requestNumber) return;
          pool = [];
          note.textContent = "";
          setState("error");
        });
    }

    sourceButtons.forEach(function (button) {
      button.addEventListener("click", function () {
        var nextSource = button.dataset.remixSource;
        if (nextSource === remixSource) return;
        setRemixSource(nextSource);
      });
    });
    if (sourceAnyButton) {
      sourceAnyButton.addEventListener("click", function () {
        setRemixSource("any");
      });
    }
    updateSourceControl();

    var tasteNote = root.querySelector("[data-discover-taste-note]");
    var tasteJump = root.querySelector("[data-remix-source-liked-jump]");
    function refreshTasteNote() {
      if (!tasteNote) return;
      var counts = sourceCounts();
      // Only suggest Liked when the user actually has likes, and only while
      // they're still on Any (switching away is the point of the note).
      tasteNote.hidden = remixSource !== "any" || counts.liked < 2;
    }
    refreshTasteNote();
    if (tasteJump) {
      tasteJump.addEventListener("click", function () {
        if (sourceCounts().liked < 1) return;
        setRemixSource("liked");
      });
    }
    document.addEventListener("theartfolio:reactionchange", refreshTasteNote);

    if (matchToggle && matchPanel) {
      matchToggle.addEventListener("click", function () {
        var expanded = matchToggle.getAttribute("aria-expanded") === "true";
        matchToggle.setAttribute("aria-expanded", expanded ? "false" : "true");
        matchPanel.hidden = expanded;
      });
      matchToggle.addEventListener("keydown", function (event) {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        matchToggle.click();
      });
    }

    form.addEventListener("change", function (event) {
      if (!event.target.matches("[data-discover-filter]")) return;
      if (event.target === colour && activeColourMatch) clearActiveColourMatch(false);
      updateUrl();
      loadPool();
    });
    if (colourMatch && matchInput && matchPalette && matchPreview && matchSwatches && matchAll && matchClear && matchStatus) {
      matchInput.addEventListener("change", function () {
        var file = matchInput.files && matchInput.files[0];
        if (!file) return;
        if (!file.type || file.type.indexOf("image/") !== 0) {
          matchStatus.textContent = "Please choose an image file.";
          return;
        }
        if (file.size > 20 * 1024 * 1024) {
          matchStatus.textContent = "Please choose an image smaller than 20 MB.";
          return;
        }

        revokeMatchObjectUrl();
        extractedPalette = [];
        activeColourMatch = null;
        matchPalette.hidden = true;
        matchStatus.textContent = "Extracting colours…";
        matchObjectUrl = URL.createObjectURL(file);
        matchPreview.src = matchObjectUrl;
        var localUrl = matchObjectUrl;
        var localImage = new Image();
        localImage.onload = function () {
          if (localUrl !== matchObjectUrl) return;
          try {
            extractedPalette = extractDominantColours(localImage);
            if (!extractedPalette.length) throw new Error("No opaque pixels");
            renderExtractedPalette();
            updateMatchSelection();
            matchStatus.textContent = "Choose a colour, or use all.";
          } catch (extractError) {
            clearUploadedMatch(false);
            matchStatus.textContent = "We could not read colours from that image. Please try another.";
          }
        };
        localImage.onerror = function () {
          if (localUrl !== matchObjectUrl) return;
          clearUploadedMatch(false);
          matchStatus.textContent = "We could not open that image. Please try another.";
        };
        localImage.src = localUrl;
      });
      matchSwatches.addEventListener("click", function (event) {
        var button = event.target.closest("[data-match-index]");
        if (!button) return;
        activateColourMatch("single", Number(button.dataset.matchIndex));
      });
      matchAll.addEventListener("click", function () {
        activateColourMatch("all");
      });
      matchClear.addEventListener("click", function () {
        clearUploadedMatch(true);
      });
      window.addEventListener("beforeunload", revokeMatchObjectUrl);
    }
    remixButtons.forEach(function (button) {
      button.addEventListener("click", function () {
        // Wildcard remains deliberately disabled in Stage 3.
        if (WILDCARD_CHANCE > 0 && Math.random() < WILDCARD_CHANCE) {
          loadPool();
          return;
        }
        choose();
      });
    });

    (function initSwipeRemix() {
      if (!card || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      var startX = 0;
      var startY = 0;
      var tracking = false;
      card.addEventListener("touchstart", function (event) {
        if (!event.changedTouches || !event.changedTouches[0]) return;
        if (event.target.closest("button, a, input, select, label, [data-pp-type], [data-view]")) return;
        tracking = true;
        startX = event.changedTouches[0].clientX;
        startY = event.changedTouches[0].clientY;
      }, { passive: true });
      card.addEventListener("touchend", function (event) {
        if (!tracking || !event.changedTouches || !event.changedTouches[0]) return;
        tracking = false;
        var dx = event.changedTouches[0].clientX - startX;
        var dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) < 72 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
        if (remix && !remix.disabled) remix.click();
      }, { passive: true });
    })();
    viewProduct.addEventListener("click", function (event) {
      if (!updateViewProductLink()) event.preventDefault();
    });
    if (shareLookButton) {
      var shareStatusTimer = null;
      shareLookButton.addEventListener("click", function () {
        copyLookLink().then(function (copied) {
          if (!shareLookStatus) return;
          window.clearTimeout(shareStatusTimer);
          shareLookStatus.textContent = copied ? "Link copied!" : "Copy unavailable";
          shareLookStatus.toggleAttribute("data-error", !copied);
          shareLookStatus.classList.add("is-visible");
          shareStatusTimer = window.setTimeout(function () {
            shareLookStatus.classList.remove("is-visible");
          }, 2200);
        });
      });
    }
    function scheduleLookRefresh() {
      requestAnimationFrame(function () {
        updateViewProductLink();
        refreshDiscoverPrice();
        updateUrl();
      });
    }
    visualiserHost.addEventListener("click", scheduleLookRefresh);
    visualiserHost.addEventListener("change", scheduleLookRefresh);
    if (drawer) {
      drawer.addEventListener("click", scheduleLookRefresh);
      drawer.addEventListener("change", scheduleLookRefresh);
    }
    function syncMoodStrip() {
      if (!moodStrip) return;
      moodStrip.querySelectorAll("[data-discover-mood-value]").forEach(function (button) {
        var value = button.getAttribute("data-discover-mood-value") || "all";
        var on = selectedMood ? value === selectedMood : value === "all";
        button.classList.toggle("is-active", on);
        button.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }
    if (moodStrip) {
      syncMoodStrip();
      moodStrip.addEventListener("click", function (event) {
        var button = event.target.closest("[data-discover-mood-value]");
        if (!button) return;
        var value = button.getAttribute("data-discover-mood-value") || "all";
        selectedMood = value === "all" ? "" : value;
        initial.mood = selectedMood;
        syncMoodStrip();
        updateUrl();
        choose();
        refreshTasteNote();
      });
    }
    if (clearBtn) {
      clearBtn.addEventListener("click", function () {
        colour.value = "";
        category.value = "all";
        orientation.value = "";
        selectedMood = "";
        initial.colour = "";
        initial.category = "all";
        initial.orientation = "";
        initial.mood = "";
        syncMoodStrip();
        updateUrl();
        loadPool();
      });
    }
    document.addEventListener("theartfolio:reactionchange", function (event) {
      updateSourceControl();
      var detail = event.detail || {};
      if (detail.resetDislikes) {
        setState("loading");
        choose();
        return;
      }
      if (
        currentProduct &&
        detail.reaction === "dislike" &&
        String(detail.id) === productReactionId(currentProduct)
      ) {
        choose();
        return;
      }
      if (remixSource === "liked") {
        choose();
        return;
      }
      if (currentProduct) {
        note.textContent = poolNote();
        setState("card");
      }
    });
    document.addEventListener("theartfolio:favouriteschange", function () {
      updateSourceControl();
      if (remixSource === "loved") choose();
    });

    // Stage 5 can read the selected handle and live visualiser choices without storage.
    root.discoveryState = {
      getProduct: function () { return currentProduct; },
      getOptions: function () { return visualiserApi.getOptions(); }
    };
    populateSelect(colour, browseColourItems(), "Any colour", initial.colour);
    if (seedRemixSource) {
      setRemixSource(seedRemixSource);
    } else {
      loadPool();
    }
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-discover]").forEach(initDiscover);
  });
})();
